import { useState, useEffect, useCallback } from 'react';
import { Box, Popover } from '@mui/material';
import { Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../supabaseClient';
import { useAuth } from '../AuthContext';
import { useDestrierTheme } from '../ThemeContext';
import { useDataCache } from '../DataCacheContext';
import { API_BASE } from '../api';
import AnalysisHeader from './AnalysisHeader';
import { trackDashboardTabClick } from '../analytics';

// ── Sidebar icons (inline SVG for crisp rendering) ──
const icons = {
  home: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  diagnosis: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 14l2 2 4-4" />
    </svg>
  ),
  weaknesses: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  strengths: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  ),
  progress: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  openings: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
      <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
    </svg>
  ),
  habits: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  rivals: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),
  training: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="12" y1="3" x2="12" y2="21" />
      <circle cx="8" cy="8" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="16" cy="16" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  ),
  settings: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  ),
  logout: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
};

const navItems = [
  { key: 'home', label: 'Home', icon: icons.home, path: '/dashboard/home' },
  { key: 'weaknesses', label: 'Weaknesses', icon: icons.weaknesses, path: '/dashboard/weaknesses' },
  { key: 'strengths', label: 'Strengths', icon: icons.strengths, path: '/dashboard/strengths' },
  { key: 'archetype', label: 'Archetype', icon: icons.diagnosis, path: '/dashboard/archetype' },
  { key: 'openings', label: 'Openings', icon: icons.openings, path: '/dashboard/openings' },
  { key: 'progress', label: 'Progress', icon: icons.progress, path: '/dashboard/progress' },
  { key: 'habits', label: 'Play Habits', icon: icons.habits, path: '/dashboard/habits' },
  { key: 'rivals', label: 'Rivals', icon: icons.rivals, path: '/dashboard/rivals' },
  { key: 'training', label: 'Training Rooms', icon: icons.training, path: '/dashboard/training' },
];

const SIDEBAR_COLLAPSED = 64;
const SIDEBAR_EXPANDED = 200;

export default function DashboardLayout() {
  const { c } = useDestrierTheme();
  const { user, signOut } = useAuth();
  const { lastUpdated, clearAll } = useDataCache();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null); // {ok, games_synced} or {error}

  useEffect(() => {
    if (!user) return;
    supabase
      .from('profiles')
      .select('chess_com_username, display_name')
      .eq('id', user.id)
      .single()
      .then(async ({ data }) => {
        let profileData = data;
        // If profile is missing or has no chess username, try to create from user metadata
        if (!profileData?.chess_com_username) {
          const meta = user.user_metadata || {};
          if (meta.chess_com_username) {
            await supabase.from('profiles').upsert({
              id: user.id,
              display_name: meta.display_name || profileData?.display_name || '',
              chess_com_username: meta.chess_com_username,
            });
            profileData = { display_name: meta.display_name || '', chess_com_username: meta.chess_com_username };
          }
        }
        if (profileData) {
          setProfile(profileData);
          if (profileData.chess_com_username && !searchParams.get('username')) {
            navigate(`${location.pathname}?username=${encodeURIComponent(profileData.chess_com_username)}`, { replace: true });
          }
        }
      });
  }, [user, navigate, location.pathname, searchParams]);

  const username = searchParams.get('username') || profile?.chess_com_username || '';
  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleSync = useCallback(async () => {
    if (syncing || !username) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const resp = await fetch(`${API_BASE}/sync-games?username=${encodeURIComponent(username)}`, { method: 'POST' });
      const data = await resp.json();
      if (resp.ok) {
        setSyncResult({ ok: true, games: data.games_synced });
        clearAll(); // Clear frontend cache so pages re-fetch with new data
      } else {
        setSyncResult({ error: data.detail || 'Sync failed' });
      }
    } catch (e) {
      setSyncResult({ error: 'Connection failed' });
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncResult(null), 5000);
    }
  }, [syncing, username, clearAll]);

  const activePath = location.pathname;
  const sidebarBg = c.sidebarBg || (c.mode === 'dark' ? '#1a1a20' : '#f0ebe6');

  // Wait for profile to load before rendering content (skip if using direct username access)
  if (!profile && !searchParams.get('username')) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: c.bg }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '50%', border: `3px solid ${c.accent}20`, borderTopColor: c.accent, animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: c.pageBg || c.bg }}>
      {/* ── Header (persistent, never unmounts) ── */}
      <AnalysisHeader username={username} profile={profile} onSignOut={handleSignOut} />

      {/* ── Sidebar (desktop only) ── */}
      <Box
        component={motion.div}
        initial={{ width: SIDEBAR_COLLAPSED }}
        animate={{ width: expanded ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED }}
        transition={{ duration: 0.12, ease: 'easeOut' }}
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
        sx={{
          height: { xs: 'calc(100vh - 50px)', md: 'calc(100vh - 60px)' },
          backgroundColor: sidebarBg,
          borderRight: `1px solid ${c.cardBorder}`,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          py: 2,
          position: 'fixed',
          top: { xs: 50, md: 60 },
          left: 0,
          zIndex: 50,
          overflow: 'hidden',
        }}
      >
        {/* Nav items */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, px: 1 }}>
          {navItems.map((item) => {
            const isActive = activePath === item.path || (item.key === 'home' && activePath === '/dashboard');
            return (
              <Box
                key={item.key}
                onClick={() => { trackDashboardTabClick(item.key); navigate(`${item.path}?username=${encodeURIComponent(username)}`); }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  height: 44,
                  px: 1.25,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  color: isActive ? c.accent : c.textMuted,
                  backgroundColor: isActive ? `${c.accent}15` : 'transparent',
                  '&:hover': {
                    backgroundColor: isActive ? `${c.accent}20` : `${c.textMuted}10`,
                    color: isActive ? c.accent : c.textDark,
                  },
                  transition: 'none',
                  minWidth: 0,
                }}
              >
                <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22 }}>
                  {item.icon}
                </Box>
                <span
                  animate={{ opacity: expanded ? 1 : 0 }}
                  transition={{ duration: 0.15, delay: expanded ? 0.1 : 0 }}
                  style={{
                    fontSize: '0.88rem',
                    fontWeight: isActive ? 600 : 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  }}
                >
                  {item.label}
                </span>
              </Box>
            );
          })}
        </Box>

        {/* Last updated + branding at bottom */}
        <Box sx={{ px: 1, pb: 1, pt: 2, borderTop: `1px solid ${c.cardBorder}` }}>
          {/* Settings */}
          <Box
            onClick={() => { trackDashboardTabClick('settings'); navigate(`/dashboard/settings?username=${encodeURIComponent(username)}`); }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              height: 44,
              px: 1.25,
              borderRadius: '12px',
              cursor: 'pointer',
              color: activePath === '/dashboard/settings' ? c.accent : c.textMuted,
              backgroundColor: activePath === '/dashboard/settings' ? `${c.accent}15` : 'transparent',
              '&:hover': {
                backgroundColor: `${c.textMuted}10`,
                color: c.textDark,
              },
              transition: 'none',
              minWidth: 0,
              mb: 0.5,
            }}
          >
            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22 }}>
              {icons.settings}
            </Box>
            <span style={{
              fontSize: '0.88rem',
              fontWeight: activePath === '/dashboard/settings' ? 600 : 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              opacity: expanded ? 1 : 0,
              transition: 'opacity 0.15s',
            }}>
              Settings
            </span>
          </Box>
          {/* Sync / Last refreshed */}
          <Box
            onClick={handleSync}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              height: 44,
              px: 1.25,
              color: syncing ? c.accent : syncResult?.ok ? '#4CAF50' : syncResult?.error ? '#f44336' : c.textMuted,
              minWidth: 0,
              cursor: syncing ? 'wait' : 'pointer',
              borderRadius: '10px',
              transition: 'background 0.15s, color 0.15s',
              '&:hover': { bgcolor: `${c.textMuted}12` },
            }}
            title={expanded ? '' : (syncing ? 'Syncing...' : 'Click to sync latest games')}
          >
            <Box sx={{
              flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22,
              animation: syncing ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 4v6h-6" />
                <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
              </svg>
            </Box>
            <span
              style={{
                fontSize: '0.88rem',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                opacity: expanded ? 1 : 0,
                transition: 'opacity 0.15s',
                display: 'flex',
                flexDirection: 'column',
                lineHeight: 1.3,
              }}
            >
              {syncing ? (
                <span style={{ fontSize: '0.85rem' }}>Syncing games...</span>
              ) : syncResult?.ok ? (
                <span style={{ fontSize: '0.85rem' }}>Synced {syncResult.games} games</span>
              ) : syncResult?.error ? (
                <span style={{ fontSize: '0.85rem' }}>{syncResult.error}</span>
              ) : (
                <>
                  <span style={{ fontSize: '0.82rem', color: c.textMuted }}>Sync games</span>
                  {lastUpdated && (
                    <span style={{ fontSize: '0.78rem' }}>
                      {lastUpdated.toLocaleDateString([], { month: 'short', day: 'numeric' })}{' '}
                      {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </>
              )}
            </span>
          </Box>

          {/* Branding */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              height: 44,
              px: 1.25,
              color: c.textMuted,
              minWidth: 0,
            }}
          >
            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 3a3 3 0 00-3 3v1H9V6a3 3 0 00-6 0v7c0 5 3.5 8 9 8s9-3 9-8V6a3 3 0 00-3-3z" />
              </svg>
            </Box>
            <span
              style={{
                fontSize: '0.88rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                opacity: expanded ? 1 : 0,
                transition: 'opacity 0.15s',
                letterSpacing: '0.04em',
              }}
            >
              <span style={{ color: c.accent }}>Destrier</span>
              <span style={{ color: c.textMuted, fontWeight: 400, marginLeft: 6, fontSize: '0.88rem' }}>v0.1</span>
            </span>
          </Box>
        </Box>
      </Box>

      {/* ── Content area ── */}
      <Box
        sx={{
          flex: 1,
          marginLeft: { xs: 0, md: `${SIDEBAR_EXPANDED}px` },
          marginRight: { xs: 0, md: activePath.includes('/training') ? 0 : `${SIDEBAR_EXPANDED - SIDEBAR_COLLAPSED}px` },
          minHeight: '100vh',
          overflow: 'auto',
          pb: { xs: '72px', md: 0 },
        }}
      >
        <Outlet context={{ profile, onSignOut: handleSignOut }} />
      </Box>

      {/* ── Bottom tab bar (mobile only) — 4 main tabs + More ── */}
      {(() => {
        const mainTabs = navItems.slice(0, 4);
        const moreTabs = navItems.slice(4);
        const moreIsActive = moreTabs.some(t => t.path === activePath);
        const moreIcon = (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="1" />
            <circle cx="12" cy="5" r="1" />
            <circle cx="12" cy="19" r="1" />
          </svg>
        );

        return (
          <>
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 50,
                backgroundColor: sidebarBg,
                borderTop: `1px solid ${c.cardBorder}`,
                justifyContent: 'space-around',
                alignItems: 'center',
                py: 1,
                px: 1,
              }}
            >
              {mainTabs.map((item) => {
                const isActive = activePath === item.path || (item.key === 'home' && activePath === '/dashboard');
                return (
                  <Box
                    key={item.key}
                    onClick={() => { trackDashboardTabClick(item.key); navigate(`${item.path}?username=${encodeURIComponent(username)}`); }}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.3,
                      cursor: 'pointer',
                      color: isActive ? c.accent : c.textMuted,
                      flex: 1,
                      '& svg': { width: 22, height: 22 },
                    }}
                  >
                    {item.icon}
                    <span style={{ fontSize: '0.62rem', fontWeight: isActive ? 700 : 500 }}>
                      {item.label}
                    </span>
                  </Box>
                );
              })}

              {/* More button */}
              <Box
                onClick={(e) => setMoreAnchor(e.currentTarget)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.3,
                  cursor: 'pointer',
                  color: moreIsActive ? c.accent : c.textMuted,
                  flex: 1,
                  '& svg': { width: 22, height: 22 },
                }}
              >
                {moreIcon}
                <span style={{ fontSize: '0.62rem', fontWeight: moreIsActive ? 700 : 500 }}>
                  More
                </span>
              </Box>
            </Box>

            {/* More popover */}
            <Popover
              open={Boolean(moreAnchor)}
              anchorEl={moreAnchor}
              onClose={() => setMoreAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              slotProps={{
                paper: {
                  sx: {
                    mb: 1,
                    borderRadius: '14px',
                    backgroundColor: sidebarBg,
                    border: `1px solid ${c.cardBorder}`,
                    boxShadow: '0 -8px 32px rgba(0,0,0,0.3)',
                    overflow: 'hidden',
                    minWidth: 180,
                  },
                },
              }}
            >
              {moreTabs.map((item) => {
                const isActive = activePath === item.path;
                return (
                  <Box
                    key={item.key}
                    onClick={() => {
                      trackDashboardTabClick(item.key);
                      setMoreAnchor(null);
                      navigate(`${item.path}?username=${encodeURIComponent(username)}`);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 2,
                      py: 1.2,
                      cursor: 'pointer',
                      color: isActive ? c.accent : c.textMuted,
                      backgroundColor: isActive ? `${c.accent}10` : 'transparent',
                      '&:hover': { backgroundColor: `${c.accent}08` },
                      '& svg': { width: 20, height: 20 },
                    }}
                  >
                    {item.icon}
                    <span style={{ fontSize: '0.88rem', fontWeight: isActive ? 700 : 500 }}>
                      {item.label}
                    </span>
                  </Box>
                );
              })}
            </Popover>
          </>
        );
      })()}
    </Box>
  );
}
